import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  UpdateMerchantConfigBody,
  updateMerchantConfig,
  AccountHolder,
  GetTimeZonesResponse,
} from "../../../services/accountService";
import { formatAmount } from "../../../utils/currency";
import useCreateAccountHolderNotesMutation from "./useCreateAccountHolderNotesMutation";
import convenienceFeeRates from "../../../data/convenienceFeeRates.json";
import useToggleRatePlanSurchargeMutation from "./useToggleRatePlanSurchargeMutation";

export default function useUpdateMerchantConfigMutation(
  accountHolderCode: string
) {
  const queryClient = useQueryClient();
  const { mutateAsync: createNote } = useCreateAccountHolderNotesMutation();
  useToggleRatePlanSurchargeMutation();

  return useMutation<string, Error, UpdateMerchantConfigBody>(
    (body) => updateMerchantConfig(body),
    {
      async onSuccess(_, updatedMerchantConfig) {
        const accountConfigData = queryClient.getQueryData<AccountHolder>([
          "accountConfig",
          accountHolderCode,
        ]);

        if (!accountConfigData) return;

        // Resolve corp names from cache for display in notes
        const corporations =
          queryClient.getQueryData<
            { accountHolderId: string; corpName: string }[]
          >(["corporations", accountConfigData.merchantAccountId]) ?? [];

        const findCorpName = (id?: string) =>
          (id &&
            corporations.find((c) => c.accountHolderId === id)?.corpName) ||
          "";

        queryClient.setQueryData(["accountConfig", accountHolderCode], {
          ...accountConfigData,
          ...updatedMerchantConfig,
        });

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "merchantStatus",
          accountConfigData.merchantStatus,
          updatedMerchantConfig.merchantStatus
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Use Interchange++ Plan",
          accountConfigData.useInterchangeRate ? "Enabled" : "Disabled",
          updatedMerchantConfig.useInterchangeRate ? "Enabled" : "Disabled"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "shopperStatement",
          accountConfigData.shopperStatement,
          updatedMerchantConfig.shopperStatement
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Use Card Transaction Limit",
          accountConfigData.useTransAmountLimit.toString(),
          updatedMerchantConfig.useTransAmountLimit.toString()
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Use ACH Transaction Limit",
          accountConfigData.useACHTransAmountLimit.toString(),
          updatedMerchantConfig.useACHTransAmountLimit.toString()
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Duplicate Detection Minutes",
          formatAmount(+accountConfigData.duplicateDetectionMinutes),
          formatAmount(+updatedMerchantConfig.duplicateDetectionMinutes)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Card Transaction Limit amount",
          formatAmount(+accountConfigData.transAmountLimit),
          formatAmount(+updatedMerchantConfig.transAmountLimit)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "ACH Transaction Limit amount",
          formatAmount(+accountConfigData.achTransAmountLimit),
          formatAmount(+updatedMerchantConfig.achTransAmountLimit)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Use Card Daily Processing Limit",
          accountConfigData.useDailyTransAmountLimit.toString(),
          updatedMerchantConfig.useDailyTransAmountLimit.toString()
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Use ACH Daily Processing Limit",
          accountConfigData.useACHDailyTransAmountLimit.toString(),
          updatedMerchantConfig.useACHDailyTransAmountLimit.toString()
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Card Daily Processing Limit amount",
          formatAmount(+accountConfigData.dailyTransAmountLimit),
          formatAmount(+updatedMerchantConfig.dailyTransAmountLimit)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "ACH Daily Processing Limit amount",
          formatAmount(+accountConfigData.achDailyTransAmountLimit),
          formatAmount(+updatedMerchantConfig.achDailyTransAmountLimit)
        );

        const timezonesData = queryClient.getQueryData<GetTimeZonesResponse>([
          "timezones",
          accountConfigData.country,
        ]);

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "timeZone",
          accountConfigData.timeZone?.code ?? "",
          updatedMerchantConfig.timeZoneCode ?? ""
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Tech Fee Rate",
          accountConfigData.useTechFee ? "Enabled" : "Disabled",
          updatedMerchantConfig.useTechFee ? "Enabled" : "Disabled"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Tech Fee Rate Percentage",
          accountConfigData.techFeeRate,
          formatAmount(+updatedMerchantConfig.techFeeRate)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Surcharge Rate",
          accountConfigData.useConvFee ? "Enabled" : "Disabled",
          updatedMerchantConfig.useConvFee ? "Enabled" : "Disabled"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Surcharge Rate Plan",
          accountConfigData.convFee === "M" ? "Match Rate Plan" : "Fixed Rate",
          updatedMerchantConfig.convFee === "M"
            ? "Match Rate Plan"
            : "Fixed Rate"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Surcharge Rate Percentage",
          accountConfigData.convFeeRate,
          formatAmount(+updatedMerchantConfig.convFeeRate)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "inBalancePlatform",
          accountConfigData.inBalancePlatform ? "Balance" : "Classic",
          updatedMerchantConfig.inBalancePlatform ? "Balance" : "Classic"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Custom Fee Rate",
          accountConfigData.useCorpFee ? "Enabled" : "Disabled",
          updatedMerchantConfig.useCorpFee ? "Enabled" : "Disabled"
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Custom Fee Percentage",
          formatAmount(+accountConfigData.corpFee),
          formatAmount(+updatedMerchantConfig.corpFeePercentage)
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Custom Fee Description",
          accountConfigData.corpFeeDescription ?? "",
          updatedMerchantConfig.corpDescription ?? ""
        );

        await createNoteIfFromAndToAreDifferent(
          accountHolderCode,
          "Custom Fee Corp ID",
          // show names, fallback to IDs when name not found
          findCorpName(accountConfigData.corpAccountHolderId) ||
            (accountConfigData.corpAccountHolderId ?? ""),
          findCorpName(updatedMerchantConfig.corpId) ||
            (updatedMerchantConfig.corpId ?? "")
        );

        queryClient.invalidateQueries(["accountConfig", accountHolderCode]);

        async function createNoteIfFromAndToAreDifferent(
          accountHolderCode: string,
          field: string,
          from: string,
          to: string
        ) {
          if (from !== to) {
            const fromValue =
              field === "convFee" ? findConvenienceFeeLabel(from) : from;
            const toValue =
              field === "convFee" ? findConvenienceFeeLabel(to) : to;
            await createNote({
              accountHolderCode,
              noteContent: [
                {
                  fieldName: field,
                  fromValue: fromValue || "null",
                  toValue: toValue || "null",
                },
              ],
            });
          }
        }
      },
    }
  );
}

function findConvenienceFeeLabel(value: string) {
  return convenienceFeeRates.find((cf) => cf.value === value)?.label ?? "";
}
